import {
  createContextMutationPrecondition,
  parseContextSession,
  type ContextSessionResponse,
  type ControlWebClient,
  type RequestOptions,
} from "@kontextwerk/control-web-client"
import type {
  KehrblattPlanningSettings,
  PlanningSettingsResponse,
  ContactSequenceTemplateId,
} from "../planning"

type Binding = Required<Pick<RequestOptions, "signal" | "isCurrent" | "mutationContext">>
const templates: readonly ContactSequenceTemplateId[] = [
  "digital_standard",
  "email_letter_standard",
  "mobile_message_first",
  "phone_assisted",
  "letter_first",
]
const fields = [
  "flex_slot_minutes",
  "flex_slots_per_day",
  "customer_appointment_window_delta_minutes",
  "customer_appointment_window_rounding_minutes",
  "appointment_letter_lead_days",
  "appointment_letter_auto_dispatch_enabled",
  "appointment_letter_todo_assignment_mode",
  "default_contact_sequence_template_id",
]
const reject = (): never => {
  throw new TypeError("Invalid planning settings contract")
}
const record = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return reject()

  return value as Record<string, unknown>
}
const settings = (value: unknown): KehrblattPlanningSettings => {
  const input = record(value)
  const bounded = (key: string, min: number, max: number) => {
    const value = input[key]

    if (typeof value !== "number" || !Number.isSafeInteger(value) || value < min || value > max)
      return reject()

    return value
  }
  const rounding = bounded("customer_appointment_window_rounding_minutes", 0, 60)
  const lead = bounded("appointment_letter_lead_days", 3, 14)
  const mode = input.appointment_letter_todo_assignment_mode

  if (
    ![0, 15, 30, 60].includes(rounding) ||
    ![3, 5, 7, 10, 12, 14].includes(lead) ||
    (mode !== "COLLECTIVE" && mode !== "PER_EMPLOYEE")
  )
    return reject()

  if (
    input.appointment_letter_auto_dispatch_enabled !== undefined &&
    typeof input.appointment_letter_auto_dispatch_enabled !== "boolean"
  )
    return reject()

  return {
    flex_slot_minutes: bounded("flex_slot_minutes", 1, 480),
    flex_slots_per_day: bounded("flex_slots_per_day", 0, 24),
    customer_appointment_window_delta_minutes: bounded(
      "customer_appointment_window_delta_minutes",
      0,
      120,
    ),
    customer_appointment_window_rounding_minutes: rounding,
    appointment_letter_lead_days: lead,
    appointment_letter_todo_assignment_mode: mode,
    ...(input.appointment_letter_auto_dispatch_enabled === undefined
      ? {}
      : {
          appointment_letter_auto_dispatch_enabled:
            input.appointment_letter_auto_dispatch_enabled as boolean,
        }),
  }
}
const template = (value: unknown): ContactSequenceTemplateId => {
  if (!templates.includes(value as ContactSequenceTemplateId)) return reject()

  return value as ContactSequenceTemplateId
}
const response = (value: unknown): PlanningSettingsResponse => {
  const input = record(value)
  const nullableText = (key: string) => {
    if (input[key] == null) return null

    if (typeof input[key] !== "string") return reject()

    return input[key] as string
  }

  if (!Array.isArray(input.available_contact_sequence_templates)) return reject()

  if (input.timezone !== undefined && typeof input.timezone !== "string") return reject()

  return {
    ...settings(input),
    timezone: input.timezone as string | undefined,
    appointment_letter_auto_dispatch_enabled_at: nullableText(
      "appointment_letter_auto_dispatch_enabled_at",
    ),
    appointment_letter_auto_dispatch_disabled_reason: nullableText(
      "appointment_letter_auto_dispatch_disabled_reason",
    ),
    default_contact_sequence_template_id: template(input.default_contact_sequence_template_id),
    available_contact_sequence_templates: input.available_contact_sequence_templates.map(
      (value) => {
        const item = record(value)

        if (
          typeof item.label !== "string" ||
          typeof item.description !== "string" ||
          !Array.isArray(item.channels) ||
          !item.channels.every((channel) => typeof channel === "string")
        )
          return reject()

        return {
          id: template(item.id),
          label: item.label,
          description: item.description,
          channels: [...item.channels],
        }
      },
    ),
  }
}

/** Prepared V2 transport. No singleton, current-cookie lookup or caller-selected tenant. */
export const createContextPlanningApi = (
  client: ControlWebClient,
  value: ContextSessionResponse,
  capture: Binding,
) => {
  const session = parseContextSession(value)
  const pair = createContextMutationPrecondition(capture.mutationContext)

  if (
    session.product_issuer !== "kehrblatt-control" ||
    session.context.kind !== "organization" ||
    session.session_id !== pair.expected_session_id ||
    session.context_revision !== pair.expected_context_revision
  )
    return reject()

  const options = {
    signal: capture.signal,
    isCurrent: capture.isCurrent,
    mutationContext: pair,
    retry: "never" as const,
  }
  const path = `/admin/planning/settings/${session.context.organization_id}`
  const current = () => {
    if (options.signal.aborted || !options.isCurrent())
      throw new DOMException("Obsolete planning context", "AbortError")
  }

  return {
    async load() {
      current()
      const result = await client.transport.request<unknown>("GET", path, options)
      current()

      return response(result)
    },
    async save(payload: KehrblattPlanningSettings) {
      current()
      const input = record(payload)

      if (Object.keys(input).some((key) => !fields.includes(key))) return reject()

      const body = {
        ...settings(input),
        ...(input.default_contact_sequence_template_id == null
          ? {}
          : {
              default_contact_sequence_template_id: template(
                input.default_contact_sequence_template_id,
              ),
            }),
      }
      const result = await client.transport.request<unknown>("PUT", path, {
        ...options,
        json: body,
        requireCsrf: true,
      })
      current()

      return response(result)
    },
  }
}
